/* eslint-disable @typescript-eslint/no-use-before-define */
import { Link } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

import useStateNonblocking from '@Hooks/useStateNonblocking'

import ProductCardSP from './ProductCardSP'
import AlertIcon from '../../assets/images/icons/alert-triangle.svg?react'
import InfoIcon from '../../assets/images/icons/info-circle.svg?react'
import { useMeQuery } from '../../store/api'
import type { Smartprice } from '../../store/types/product'
import { REPRICEABLE_SP_STATUSES } from '../../utils/constants'
import SmartPriceLink from '../menu/items/smart-price-link'
import ShopifyTableTag from '../shopify/ShopifyTableTag'

interface ProductCardPriceInfoProps {
  smartprice: Smartprice
  productId: number
  isInFreezePeriod?: boolean
  isLoading?: boolean
}

function ProductCardPriceInfo({
  smartprice,
  productId,
  isInFreezePeriod = false,
  isLoading,
}: ProductCardPriceInfoProps): JSX.Element {
  const [sp, isSpPending] = useStateNonblocking<Record<string, any> | null>(
    () =>
      !!smartprice && Object.keys(smartprice).length > 0
        ? {
            ruleSetID: smartprice?.ruleSetID,
            price: smartprice?.price,
            info: smartprice?.info,
            hasSmartPriceRule: smartprice?.status !== -1,
            hasSmartPriceDisabledRule: smartprice?.status === -98,
            hasSmartPriceError: smartprice?.infoType === 'error',
            hasSmartPriceWarning: smartprice?.infoType === 'warning',
            ruleText: !(smartprice?.status !== -1)
              ? 'SP cannot be calculated as there is no rule applied to this product.'
              : `${`<p style="font-size:0.95em;line-height:1.45em;margin-bottom:.5em;"><i>${isInFreezePeriod ? 'It’s competitor tracking time. Your price will be updated with the calculated Smart Price soon. <br><a href="https://helpcenter.prisync.com/hc/en-us/articles/115002988993-How-to-set-up-SmartPrice-rules" target="blank">Learn more</a>' : 'Your prices will be instantly updated with the calculated SmartPrice.'}</i></p>`}${
                  smartprice?.ruleSetName
                    ? `${smartprice.ruleSetName} is applied.`
                    : smartprice.ruleText
                      ? smartprice.ruleText
                      : ''
                }`,
          }
        : null,
    [smartprice],
    null,
  )

  const { isShopify, shopifySetPrices } = useMeQuery(undefined, {
    selectFromResult: ({ data }) => ({
      isShopify: !!data?.isShopify,
      shopifySetPrices: data?.shopify_set_prices,
    }),
  })
  const isRepricingOff = !shopifySetPrices
  if (isLoading || isSpPending || !sp) {
    return <ProductCardPriceInfoLoading />
  }

  // Surface the "repricing off" indicator only where an SP value would actually
  // be applied to the store. This mirrors the backend's repriceWithSmartPrice(),
  // which only reprices when the status is SP_NO_ERROR or SP_COST_ZERO — any
  // other status (no rule, disabled, profit/threshold violations, errors…) is
  // never pushed, so the indicator would be meaningless there.
  // Shopify-only, since shopify_set_prices is meaningless for other sources.
  const canReprice = REPRICEABLE_SP_STATUSES.includes(
    Number(smartprice?.status),
  )
  const showRepricingOff = isShopify && isRepricingOff && canReprice

  return (
    <>
      <ProductCardSP
        productId={productId}
        ruleSetID={smartprice?.ruleSetID}
        {...(!sp?.hasSmartPriceDisabledRule
          ? {
              ruleText: sp?.ruleText,
            }
          : {})}
      />
      {sp?.hasSmartPriceDisabledRule ? (
        <p
          className="text-gray-500 text-sm"
          dangerouslySetInnerHTML={{
            __html: sp?.price,
          }}
        ></p>
      ) : !sp?.hasSmartPriceRule ? (
        <p className="font-semibold text-primary-600 text-sm">
          <SmartPriceLink to="/dynamic-pricing/add-new-rule">
            Set a pricing rule to see a SmartPrice suggestion.
          </SmartPriceLink>
        </p>
      ) : sp?.hasSmartPriceError ? (
        <>
          <p className="text-gray-500 text-sm">
            {sp.price}
            <AlertIcon
              data-tooltip-id={`priceinfo-tooltip-${productId}`}
              className="inline ml-1 cursor-pointer align-bottom w-[1.15em]"
            />
          </p>
          <Tooltip id={`priceinfo-tooltip-${productId}`}>
            <div
              className="w-40"
              dangerouslySetInnerHTML={{
                __html: sp.info,
              }}
            ></div>
          </Tooltip>
        </>
      ) : (
        <div className="flex items-center gap-x-2">
          <p className="font-semibold text-xl">{sp.price}</p>
          {showRepricingOff && (
            <ShopifyTableTag
              color="yellow"
              className="inline-flex items-center gap-x-1"
            >
              <InfoIcon className="w-[1.1em] [&>path]:stroke-yellow-700" />
              Passive
            </ShopifyTableTag>
          )}
        </div>
      )}
      {sp?.hasSmartPriceWarning && (
        <p className="text-gray-500 text-sm">
          <AlertIcon className="inline mr-1 align-bottom w-[1.15em]" />
          {sp.info}
        </p>
      )}
      {showRepricingOff ? (
        <p className="text-sm text-gray-500">
          Repricing is off —{' '}
          <Link
            to="/settings/shopify-settings"
            className="text-primary-600 font-medium"
          >
            go to settings
          </Link>
        </p>
      ) : (
        !sp?.hasSmartPriceWarning && <p className="text-sm">&nbsp;</p>
      )}
    </>
  )
}

export default ProductCardPriceInfo

function ProductCardPriceInfoLoading(): JSX.Element {
  return (
    <div className="flex flex-col w-full h-full space-y-6 py-2.5">
      <div className="flex flex-row gap-x-2 w-full justify-center">
        <div className="animate-pulse bg-gray-200 h-6 w-full rounded-md"></div>
      </div>
      <div className="flex flex-row w-full justify-between">
        <div className="animate-pulse bg-gray-200 h-6 w-5/12 rounded-md"></div>
        <div className="animate-pulse bg-gray-200 h-6 w-5/12 rounded-md"></div>
      </div>
    </div>
  )
}
